import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FittedSignatureCanvas from './FittedSignatureCanvas';
import CancelNegotiationButton from './CancelNegotiationButton';
import useRateCatalog from '../../hooks/useRateCatalog';
import { useOrg } from '../../context/OrgContext';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Chip,
  Stack,
  useMediaQuery
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Print as PrintIcon,
  Send as SendIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';

// Define theme colors
const themeColors = {
  primary: '#1a237e', // Dark blue
  white: '#ffffff',
  lightGray: '#f5f5f5',
  mediumGray: '#e0e0e0',
  textColor: '#333333'
};

// Custom styles
const styles = {
  header: {
    backgroundColor: themeColors.primary,
    color: themeColors.white,
    padding: '20px',
    borderRadius: '4px',
    marginBottom: '24px'
  },
  logo: {
    height: '60px',
    marginRight: '15px'
  },
  sectionTitle: {
    color: themeColors.primary,
    borderBottom: `2px solid ${themeColors.mediumGray}`,
    paddingBottom: '8px',
    marginBottom: '16px',
    fontWeight: 600
  },
  tableHeader: {
    backgroundColor: themeColors.primary,
    '& th': {
      color: themeColors.white,
      fontWeight: 'bold'
    }
  },
  signatureSection: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '20px',
    flexWrap: 'wrap'
  },
  signatureBlock: {
    flex: '1 1 280px',
    minWidth: 0,
    border: `1px solid ${themeColors.mediumGray}`,
    padding: '20px',
    borderRadius: '4px',
    marginBottom: '16px'
  },
  signatureCanvas: {
    backgroundColor: themeColors.lightGray,
    border: `1px solid ${themeColors.mediumGray}`,
    margin: '10px 0'
  },
  footer: {
    marginTop: '40px',
    padding: '15px',
    backgroundColor: themeColors.lightGray,
    color: themeColors.primary,
    textAlign: 'center',
    borderRadius: '4px',
    fontSize: '14px'
  },
  primaryButton: {
    backgroundColor: themeColors.primary,
    color: themeColors.white,
    '&:hover': {
      backgroundColor: '#2a3490' // Slightly lighter blue
    }
  },
  secondaryButton: {
    color: themeColors.primary,
    borderColor: themeColors.primary
  }
};

// Helper function to format service names
const formatServiceName = (serviceName) => {
  // Replace camelCase with spaces
  const withSpaces = serviceName.replace(/([A-Z])/g, ' $1').replace(/([a-z])([A-Z])/g, '$1 $2');
  
  // Handle special cases
  const specialCases = {
    'minor Road Service': 'Minor Road Service',
    'jump Start': 'Jump Start',
    'tire Change': 'Tire Change',
    'fuel Delivery': 'Fuel Delivery',
    'battery Test': 'Battery Test',
    'battery Replacement': 'Battery Replacement',
    'tow Medium': 'Tow - Medium Duty',
    'tow Heavy': 'Tow - Heavy Duty',
    'tow': 'Tow - Light Duty',
    'enroute For Road Services Only': 'En Route for Road Services Only (per mile)',
    'enroute For Tow Services': 'En Route for Tow Services (per mile, loaded miles included)',
    'go Jacks': 'Go-Jacks',
    'drive Shaft Removal': 'Drive Shaft Removal',
    'air Bag Recovery Unit': 'Air Bag Recovery Unit',
    'clean Up Fee': 'Clean Up Fee',
    'oil Dry': 'Oil Dry',
    'extra Man Helpers': 'Extra Man/Helpers',
    'gate Fee': 'Gate Fee',
    'release Fee': 'Release Fee',
    'processing Fee': 'Processing Fee',
    'paid Out Charges': 'Paid Out Charges',
    'pick Up Key': 'Pick Up Key',
  };
  
  // Check for special cases first
  for (const [key, value] of Object.entries(specialCases)) {
    if (serviceName.toLowerCase() === key.toLowerCase()) {
      return value;
    }
  }
  
  // Otherwise capitalize first letter of each word
  return withSpaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const hasValue = (value) => value !== null && value !== undefined;

// The rate both sides currently stand on: an accepted proposal, else the original
const agreedRate = (item) =>
  (item.negotiationStatus === 'accepted' && hasValue(item.proposedRate)) ? item.proposedRate : item.originalRate;
const agreedFreeMiles = (item) =>
  (item.negotiationStatus === 'accepted' && hasValue(item.proposedFreeMiles)) ? item.proposedFreeMiles : item.freeMiles;

/** True when the provider's entries differ from the current agreed rates (i.e. a new proposal). */
const rateHasChanges = (rateItems, proposedRates) => rateItems.some(item => {
  const entry = proposedRates[`${item.serviceType}-${item.dutyLevel}`];
  if (!entry) return false;
  if (parseFloat(entry.rate) !== agreedRate(item)) return true;
  const miles = parseFloat(entry.freeMiles);
  return Number.isFinite(miles) && miles !== Number(agreedFreeMiles(item) ?? 0);
});

const isPercentItem = (item) => item.serviceType === 'goneOnArrival';
const formatAmount = (item, value) => {
  const n = Number(value) || 0;
  return isPercentItem(item) ? `${n.toFixed(1)}%` : `$${n.toFixed(2)}`;
};
const showsFreeMiles = (item) => item.freeMiles > 0 || /^enroute/i.test(item.serviceType);
const DUTY_LABELS = { light: 'Light Duty', medium: 'Medium Duty', heavy: 'Heavy Duty', all: 'All Levels' };

/** Where an item stands in negotiation, from the provider's point of view. */
const itemStatus = (item) => {
  const proposedChange = hasValue(item.proposedRate) && item.proposedRate !== item.originalRate;
  if (!proposedChange) return null;
  if (item.negotiationStatus === 'accepted') return { label: 'Accepted', color: 'success' };
  if (item.negotiationStatus === 'rejected') {
    return { label: `Declined (you proposed ${formatAmount(item, item.proposedRate)})`, color: 'error' };
  }
  return { label: 'Awaiting review', color: 'default' };
};

/**
 * One block of rates. Wide screens get a table; phones get one card per rate
 * so the current rate, the inputs and the status fit without sideways scrolling.
 */
const RateSection = ({ title, items, finalized, editable, proposedRates, onChange, isMobile, labelOf = formatServiceName }) => {
  if (items.length === 0) return null;
  const anyFreeMiles = items.some(showsFreeMiles);
  const anyDuty = items.some(item => DUTY_LABELS[item.dutyLevel]);

  const rateInput = (item, entry, fullWidth) => (
    <TextField
      type="number"
      label={fullWidth ? 'Your rate' : undefined}
      inputProps={{ min: 0, step: isPercentItem(item) ? 0.1 : 0.01, inputMode: 'decimal', style: { textAlign: 'right' } }}
      value={entry.rate}
      onChange={(e) => onChange(item.serviceType, item.dutyLevel, 'rate', e.target.value)}
      size="small"
      fullWidth={fullWidth}
      disabled={!editable}
      sx={fullWidth ? undefined : { width: 130 }}
      InputProps={{
        startAdornment: isPercentItem(item) ? null : <InputAdornment position="start">$</InputAdornment>,
        endAdornment: isPercentItem(item) ? <InputAdornment position="end">%</InputAdornment> : null,
      }}
    />
  );
  const milesInput = (item, entry, fullWidth) => (
    <TextField
      type="number"
      label={fullWidth ? 'Free miles' : undefined}
      inputProps={{ min: 0, step: 1, inputMode: 'numeric', style: { textAlign: 'right' } }}
      value={entry.freeMiles}
      onChange={(e) => onChange(item.serviceType, item.dutyLevel, 'freeMiles', e.target.value)}
      size="small"
      fullWidth={fullWidth}
      disabled={!editable}
      sx={fullWidth ? undefined : { width: 100 }}
    />
  );

  const rows = items.map((item, index) => ({
    item,
    key: item._id || `${item.serviceType}-${item.dutyLevel}-${index}`,
    entry: proposedRates[`${item.serviceType}-${item.dutyLevel}`] || { rate: agreedRate(item), freeMiles: agreedFreeMiles(item) },
    status: finalized ? null : itemStatus(item),
  }));

  return (
    <Paper sx={{ p: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }} elevation={3}>
      <Typography variant="h5" sx={{ ...styles.sectionTitle, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>{title}</Typography>
      {isMobile ? (
        <Stack spacing={1.5}>
          {rows.map(({ item, key, entry, status }) => (
            <Box
              key={key}
              sx={{
                border: `1px solid ${themeColors.mediumGray}`,
                borderLeft: `4px solid ${status?.color === 'success' ? '#4caf50' : status?.color === 'error' ? '#f44336' : themeColors.primary}`,
                borderRadius: 1,
                p: 1.5
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>{labelOf(item.serviceType)}</Typography>
              {DUTY_LABELS[item.dutyLevel] && (
                <Typography variant="body2" color="text.secondary">{DUTY_LABELS[item.dutyLevel]}</Typography>
              )}
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {finalized ? 'Rate' : 'Current rate'}: <strong>{formatAmount(item, agreedRate(item))}</strong>
                {showsFreeMiles(item) && <> · {agreedFreeMiles(item) ?? 0} free miles</>}
              </Typography>
              {!finalized && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                  {rateInput(item, entry, true)}
                  {showsFreeMiles(item) && milesInput(item, entry, true)}
                </Box>
              )}
              {status && <Chip size="small" label={status.label} color={status.color} variant="outlined" sx={{ mt: 1, maxWidth: '100%' }} />}
            </Box>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={styles.tableHeader}>
                <TableCell>Service</TableCell>
                {anyDuty && <TableCell>Duty Level</TableCell>}
                <TableCell align="right">{finalized ? 'Rate' : 'Current Rate'}</TableCell>
                {!finalized && <TableCell align="right">Your Rate</TableCell>}
                {anyFreeMiles && <TableCell align="right">Free Miles</TableCell>}
                {anyFreeMiles && !finalized && <TableCell align="right">Your Free Miles</TableCell>}
                {!finalized && <TableCell align="center">Status</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ item, key, entry, status }) => (
                <TableRow
                  key={key}
                  sx={{
                    backgroundColor: status?.color === 'success' ? 'rgba(76, 175, 80, 0.1)'
                      : status?.color === 'error' ? 'rgba(244, 67, 54, 0.1)' : 'inherit'
                  }}
                >
                  <TableCell>{labelOf(item.serviceType)}</TableCell>
                  {anyDuty && <TableCell>{DUTY_LABELS[item.dutyLevel] || ''}</TableCell>}
                  <TableCell align="right">{formatAmount(item, agreedRate(item))}</TableCell>
                  {!finalized && <TableCell align="right">{rateInput(item, entry, false)}</TableCell>}
                  {anyFreeMiles && (
                    <TableCell align="right">{showsFreeMiles(item) ? (agreedFreeMiles(item) ?? 0) : 'N/A'}</TableCell>
                  )}
                  {anyFreeMiles && !finalized && (
                    <TableCell align="right">{showsFreeMiles(item) ? milesInput(item, entry, false) : 'N/A'}</TableCell>
                  )}
                  {!finalized && (
                    <TableCell align="center">
                      {status
                        ? <Typography variant="body2" sx={{ fontWeight: 'bold', color: status.color === 'default' ? 'text.secondary' : `${status.color}.main` }}>{status.label}</Typography>
                        : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No change</Typography>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

const RateAgreementPage = () => {
  const { name: orgName, legalName, logoUrl, isPlatform, supportEmail, supportPhone } = useOrg();
  const isMobile = useMediaQuery('(max-width:700px)');
  const rateCatalog = useRateCatalog();
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Problems with the provider's entries: shown by the Submit button, not in place of the page
  const [formError, setFormError] = useState(null);
  const [rateAgreement, setRateAgreement] = useState(null);
  const [proposedRates, setProposedRates] = useState({});
  const [signatureName, setSignatureName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const signatureRef = useRef(null);
  const [signatureType, setSignatureType] = useState('draw'); // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState('');

  // Fetch rate agreement data
  useEffect(() => {
    const fetchRateAgreement = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/rate-agreement/${token}`);
        setRateAgreement(response.data.rateAgreement);
        
        // Initialize proposed rates with original values
        const initialProposedRates = {};
        response.data.rateAgreement.rateItems.forEach(item => {
          // If the agreement has been negotiated, use the appropriate rate as the starting point
          // For accepted items, use the accepted rate (proposedRate)
          // For rejected items, use the original rate
          // This ensures that after rejection/acceptance, the applicant starts with the correct values
          const useOriginalRate = response.data.rateAgreement.status === 'negotiated';
          
          // Determine the rate to use based on negotiation status
          let rateToUse = item.originalRate;
          let freeMilesToUse = item.freeMiles;
          
          if (useOriginalRate) {
            // If item was accepted in negotiation, use the accepted rate (proposedRate)
            if (item.negotiationStatus === 'accepted' && item.proposedRate !== null) {
              rateToUse = item.proposedRate;
              freeMilesToUse = item.proposedFreeMiles !== null ? item.proposedFreeMiles : item.freeMiles;
            } else {
              // For rejected or pending items, use the original rate
              rateToUse = item.originalRate;
              freeMilesToUse = item.freeMiles;
            }
          } else {
            // For non-negotiated agreements, use proposed rate if available, otherwise original
            rateToUse = item.proposedRate !== null ? item.proposedRate : item.originalRate;
            freeMilesToUse = item.proposedFreeMiles !== null ? item.proposedFreeMiles : item.freeMiles;
          }
          
          initialProposedRates[`${item.serviceType}-${item.dutyLevel}`] = {
            rate: rateToUse,
            freeMiles: freeMilesToUse
          };
        });
        setProposedRates(initialProposedRates);
        
        // Check if already submitted
        if (response.data.rateAgreement.status !== 'generated' &&
            response.data.rateAgreement.status !== 'viewed') {
          setSubmitted(true);
        }
        
        // Clear signature if the agreement has been negotiated
        if (response.data.rateAgreement.status === 'negotiated') {
          setSignatureName('');
          if (signatureRef.current) {
            signatureRef.current?.clear();
          }
          setTypedSignature('');
          // Reset submitted state to allow re-submission
          setSubmitted(false);
        }
        
      } catch (err) {
        console.error('Error fetching rate agreement:', err);
        setError(err.response?.data?.message || 'Failed to fetch rate agreement');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRateAgreement();
  }, [token]);

  // Handle rate input change
  const handleRateChange = (serviceType, dutyLevel, field, value) => {
    setProposedRates(prev => ({
      ...prev,
      [`${serviceType}-${dutyLevel}`]: {
        ...prev[`${serviceType}-${dutyLevel}`],
        [field]: value
      }
    }));
  };

  // Clear signature
  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (!signatureName.trim()) {
        setFormError('Please enter your name for the signature');
        return;
      }
      
      // Check signature based on type
      if (signatureType === 'draw' && (!signatureRef.current || signatureRef.current.isEmpty())) {
        setFormError('Please sign the agreement');
        return;
      }
      
      if (signatureType === 'type' && !typedSignature.trim()) {
        setFormError('Please enter your e-signature');
        return;
      }
      
      setLoading(true);
      
      // Format proposed rates for submission
      const formattedRates = Object.entries(proposedRates).map(([key, value]) => {
        const [serviceType, dutyLevel] = key.split('-');
        return {
          serviceType,
          dutyLevel,
          rate: parseFloat(value.rate),
          freeMiles: value.freeMiles !== undefined ? parseFloat(value.freeMiles) : undefined
        };
      });
      
      if (formattedRates.some(rate => !Number.isFinite(rate.rate) || rate.rate < 0)) {
        setFormError('Enter a rate for every service before signing.');
        setLoading(false);
        return;
      }

      // Unchanged from the current agreed rates = accepting them as they stand
      const hasRateChanges = rateHasChanges(rateAgreement.rateItems, proposedRates);
      
      // Get signature data based on type
      let signatureData;
      if (signatureType === 'draw') {
        signatureData = signatureRef.current.toDataURL();
      } else {
        // Create a canvas for the typed signature
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 500;
        canvas.height = 200;
        
        // Set background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Set text style
        ctx.font = '40px "Brush Script MT", cursive';
        ctx.fillStyle = 'black';
        
        // Center the text
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);
        
        // Convert to data URL
        signatureData = canvas.toDataURL();
      }
      
      // If no rate changes, mark as finalized directly
      const endpoint = hasRateChanges
        ? `${process.env.REACT_APP_API_URL}/api/rate-agreement/${token}/submit`
        : `${process.env.REACT_APP_API_URL}/api/rate-agreement/${token}/finalize`;
      
      const response = await axios.post(endpoint, {
        proposedRates: formattedRates,
        signature: {
          name: signatureName,
          data: signatureData
        }
      });

      setFormError(null);
      setRateAgreement(prev => ({ ...prev, status: hasRateChanges ? 'submitted' : 'finalized' }));
      setSubmitted(true);

    } catch (err) {
      console.error('Error submitting rate agreement:', err);
      setFormError(err.response?.data?.message || 'Failed to submit rate agreement');
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    try {
      window.open(`${process.env.REACT_APP_API_URL}/api/rate-agreement/${token}/export/pdf`, '_blank');
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Failed to download PDF');
    }
  };

  // Handle Excel download
  const handleDownloadExcel = async () => {
    try {
      window.open(`${process.env.REACT_APP_API_URL}/api/rate-agreement/${token}/export/excel`, '_blank');
    } catch (err) {
      console.error('Error downloading Excel:', err);
      setError('Failed to download Excel');
    }
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // One line per rate, in catalog order, split into the two sections
  const groupRateItemsByCategory = (rateItems) => {
    const items = rateCatalog.sortItems(rateCatalog.dedupeItems(rateItems));
    const standardRates = items.filter(item => !item.dutyLevel.includes('additional'));
    const additionalCharges = items.filter(item => item.dutyLevel.includes('additional'));
    return { standardRates, additionalCharges };
  };
  const labelOf = (key) => rateCatalog.byKey[key]?.label || formatServiceName(key);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: '800px', margin: '0 auto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!rateAgreement) {
    return (
      <Box sx={{ p: 4, maxWidth: '800px', margin: '0 auto' }}>
        <Alert severity="error">Rate agreement not found</Alert>
      </Box>
    );
  }

  const { standardRates, additionalCharges } = groupRateItemsByCategory(rateAgreement.rateItems);
  const isFinalized = rateAgreement.status === 'finalized';
  const isReturned = rateAgreement.status === 'negotiated';
  const isCanceled = rateAgreement.status === 'closed';
  // White Knight staff opening the provider's link from the portal: read-only, no signing
  const staffView = new URLSearchParams(window.location.search).get('view') === 'staff';
  const canSign = !submitted && !staffView && !isCanceled;
  const hasProposedChanges = rateHasChanges(rateAgreement.rateItems, proposedRates);
  // Anyone signed in to the portal may ask to withdraw; the server checks who they are
  const canCancel = !isFinalized && !isCanceled && !!localStorage.getItem('token');

  return (
    <Box sx={{ p: { xs: 1.5, sm: 4 }, maxWidth: '1200px', margin: '0 auto' }} className="rate-agreement-page">
      {/* New Header with Logo and Company Name */}
      <Paper sx={{ ...styles.header, mb: { xs: 2, sm: 4 }, p: { xs: 1.5, sm: '20px' } }} elevation={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                component="img"
                src={logoUrl || '/images/logo.png'}
                alt={`${legalName} Logo`}
                sx={{ height: { xs: 40, sm: 60 }, mr: { xs: 1, sm: '15px' } }}
              />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '2.125rem' } }}>
                  {legalName.toUpperCase()}
                </Typography>
                <Typography variant="subtitle1">
                  Professional Roadside Assistance Services
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Facility Information */}
      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }} elevation={3}>
        <Typography variant="h5" sx={styles.sectionTitle}>SERVICE PROVIDER RATE AGREEMENT</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ color: themeColors.primary, mb: 1 }}>Facility Information</Typography>
            <Typography variant="h6">{rateAgreement.facilityName}</Typography>
            <Typography variant="body1">
              {rateAgreement.facilityLocation.address1}
              {rateAgreement.facilityLocation.address2 && `, ${rateAgreement.facilityLocation.address2}`}
            </Typography>
            <Typography variant="body1">
              {rateAgreement.facilityLocation.city}, {rateAgreement.facilityLocation.state} {rateAgreement.facilityLocation.zip}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ color: themeColors.primary, mb: 1 }}>Agreement Details</Typography>
            <Typography variant="body1">
              <strong>Date:</strong> {new Date().toLocaleDateString()}
            </Typography>
            <Typography variant="body1">
              <strong>Agreement ID:</strong> {rateAgreement.id}
            </Typography>
            <Typography variant="body1">
              <strong>Expires:</strong> {new Date(rateAgreement.expiresAt).toLocaleDateString()}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {isCanceled ? (
        <Alert severity="warning" sx={{ mb: { xs: 2, sm: 4 } }}>
          This negotiation was canceled. The rates on file remain in effect; a new agreement can be generated at any time.
        </Alert>
      ) : submitted && (
        <Alert severity="success" sx={{ mb: { xs: 2, sm: 4 } }}>
          {isFinalized
            ? 'This rate agreement is finalized. The rates below are in effect.'
            : `This rate agreement has been submitted. ${orgName} will review your rates and email you. Thank you!`}
        </Alert>
      )}

      {canCancel && (
        <Box sx={{ mb: { xs: 2, sm: 4 }, display: 'flex', justifyContent: 'flex-end' }}>
          <CancelNegotiationButton
            agreementId={rateAgreement.id}
            onCanceled={() => {
              setRateAgreement(prev => ({ ...prev, status: 'closed' }));
              setSubmitted(true);
            }}
          />
        </Box>
      )}

      {staffView && !submitted && (
        <Alert severity="info" sx={{ mb: { xs: 2, sm: 4 } }}>
          {isReturned
            ? "Staff view: this is the service provider's copy. It is waiting for them to accept your reviewed rates and sign, or to propose new rates. Only they can sign it."
            : "Staff view: this is the service provider's copy. Only they can sign it."}
        </Alert>
      )}

      {canSign && (
        <Alert severity={isReturned ? 'warning' : 'info'} sx={{ mb: { xs: 2, sm: 4 } }}>
          {isReturned
            ? `${orgName} reviewed your proposed rates. Accepted rates are now the current rate; declined rates went back to the original rate. Sign below to accept these rates, or change any rate to propose it again.`
            : 'Review the rates below. To propose a different rate, enter it in the "Your Rate" box. Then sign at the bottom.'}
        </Alert>
      )}

      {/* Standard Rates Table */}
      <RateSection
        title="Service Rates"
        items={standardRates}
        finalized={rateAgreement.status === 'finalized'}
        editable={canSign}
        proposedRates={proposedRates}
        onChange={handleRateChange}
        isMobile={isMobile}
        labelOf={labelOf}
      />

      {/* Additional Charges Table */}
      <RateSection
        title="Additional Charges"
        items={additionalCharges}
        finalized={rateAgreement.status === 'finalized'}
        editable={canSign}
        proposedRates={proposedRates}
        onChange={handleRateChange}
        isMobile={isMobile}
        labelOf={labelOf}
      />

      {/* Enhanced Signature Section with Both Parties */}
      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }} elevation={3}>
        <Typography variant="h5" sx={styles.sectionTitle}>Signatures</Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          This agreement must be signed by both the Service Provider and a {orgName} representative.
        </Typography>
        
        <Box sx={styles.signatureSection}>
          {/* Service Provider Signature Block */}
          <Box sx={styles.signatureBlock}>
            <Typography variant="h6" sx={{ color: themeColors.primary, mb: 2 }}>
              SERVICE PROVIDER
            </Typography>
            
            {staffView && !submitted ? (
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                Not signed yet. Waiting for the service provider.
              </Typography>
            ) : submitted ? (
              <Box>
                <Typography variant="subtitle1"><strong>Signed by:</strong> {rateAgreement.serviceProviderSignature?.name}</Typography>
                <Typography variant="subtitle2">
                  <strong>Date:</strong> {new Date(rateAgreement.serviceProviderSignature?.date).toLocaleDateString()}
                </Typography>
                {rateAgreement.serviceProviderSignature?.signatureData && (
                  <Box sx={{ mt: 2, border: `1px solid ${themeColors.mediumGray}`, p: 2 }}>
                    <img
                      src={rateAgreement.serviceProviderSignature.signatureData}
                      alt="Service Provider Signature"
                      style={{ maxWidth: '100%', maxHeight: '150px' }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                <TextField
                  label="Print Name"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  error={!signatureName && !!formError}
                  helperText={!signatureName && formError ? "Name is required" : ""}
                />
                
                {/* Signature Type Selection */}
                <Box sx={{ mt: 2, mb: 2, display: 'flex', gap: 2 }}>
                  <Button
                    variant={signatureType === 'draw' ? 'contained' : 'outlined'}
                    sx={signatureType === 'draw' ? styles.primaryButton : styles.secondaryButton}
                    onClick={() => setSignatureType('draw')}
                  >
                    Draw Signature
                  </Button>
                  <Button
                    variant={signatureType === 'type' ? 'contained' : 'outlined'}
                    sx={signatureType === 'type' ? styles.primaryButton : styles.secondaryButton}
                    onClick={() => setSignatureType('type')}
                  >
                    E-Signature
                  </Button>
                </Box>
                
                {signatureType === 'draw' ? (
                  <>
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Sign below:</Typography>
                    <Box
                      sx={{
                        ...styles.signatureCanvas,
                        height: '200px',
                        mb: 2,
                        position: 'relative', // Add position relative
                        width: '100%' // Ensure the box takes full width
                      }}
                    >
                      <FittedSignatureCanvas ref={signatureRef} height={200} />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Button
                        variant="outlined"
                        sx={styles.secondaryButton}
                        startIcon={<ClearIcon />}
                        onClick={clearSignature}
                      >
                        Clear Signature
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Type your signature:</Typography>
                    <TextField
                      label="E-Signature"
                      value={typedSignature}
                      onChange={(e) => setTypedSignature(e.target.value)}
                      fullWidth
                      margin="normal"
                      required
                      error={!typedSignature && !!formError}
                      helperText={!typedSignature && formError ? "E-signature is required" : ""}
                      InputProps={{
                        sx: {
                          fontFamily: '"Brush Script MT", cursive',
                          fontSize: '24px'
                        }
                      }}
                    />
                    <Box
                      sx={{
                        ...styles.signatureCanvas,
                        height: '100px',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: '"Brush Script MT", cursive',
                        fontSize: '32px'
                      }}
                    >
                      {typedSignature || 'Your signature will appear here'}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
          
          {/* White Knight Representative Signature Block */}
          <Box sx={styles.signatureBlock}>
            <Typography variant="h6" sx={{ color: themeColors.primary, mb: 2 }}>
              {legalName.toUpperCase()}
            </Typography>
            
            {/* Check for either representativeSignature or adminSignature */}
            {(rateAgreement.representativeSignature?.name || rateAgreement.adminSignature?.name) ? (
              <Box>
                <Typography variant="subtitle1">
                  <strong>Signed by:</strong> {rateAgreement.representativeSignature?.name || rateAgreement.adminSignature?.name}
                </Typography>
                <Typography variant="subtitle2">
                  <strong>Date:</strong> {new Date(rateAgreement.representativeSignature?.date || rateAgreement.adminSignature?.date).toLocaleDateString()}
                </Typography>
                {/* Check for signature data in either representativeSignature or adminSignature */}
                {((rateAgreement.representativeSignature?.signatureData &&
                   rateAgreement.representativeSignature.signatureData.startsWith('data:image')) ||
                  (rateAgreement.adminSignature?.signatureData &&
                   rateAgreement.adminSignature.signatureData.startsWith('data:image'))) ? (
                  <Box sx={{ mt: 2, border: `1px solid ${themeColors.mediumGray}`, p: 2 }}>
                    <img
                      src={rateAgreement.representativeSignature?.signatureData || rateAgreement.adminSignature?.signatureData}
                      alt={`${orgName} Representative Signature`}
                      style={{ maxWidth: '100%', maxHeight: '150px' }}
                    />
                  </Box>
                ) : ((rateAgreement.representativeSignature?.name || rateAgreement.adminSignature?.name) && (
                  <Box sx={{ mt: 2, border: `1px solid ${themeColors.mediumGray}`, p: 2 }}>
                    <Typography variant="body1" sx={{ fontFamily: '"Brush Script MT", cursive', fontSize: '32px', textAlign: 'center' }}>
                      {rateAgreement.representativeSignature?.name || rateAgreement.adminSignature?.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box>
                {rateAgreement.status === 'negotiated' ? (
                  // If the agreement has been negotiated, show that it's been reviewed
                  <Box>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      This agreement has been reviewed by a {orgName} representative.
                      Please review the accepted/rejected rates and submit your response.
                    </Typography>
                    <Box sx={{
                      ...styles.signatureCanvas,
                      height: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      backgroundColor: 'rgba(76, 175, 80, 0.1)'
                    }}>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        Reviewed and Returned for Your Response
                      </Typography>
                    </Box>
                    <Typography variant="subtitle2">
                      <CalendarIcon sx={{ fontSize: 'small', mr: 1, verticalAlign: 'middle' }} />
                      Date: {new Date().toLocaleDateString()}
                    </Typography>
                  </Box>
                ) : (
                  // Default state - pending review
                  <Box>
                    <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                      A {orgName} representative will review and sign this agreement after submission.
                    </Typography>
                    <Box sx={{
                      ...styles.signatureCanvas,
                      height: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}>
                      <Typography variant="body2" sx={{ color: themeColors.mediumGray, fontStyle: 'italic' }}>
                        Pending Review
                      </Typography>
                    </Box>
                    <Typography variant="subtitle2" sx={{ color: themeColors.mediumGray }}>
                      <CalendarIcon sx={{ fontSize: 'small', mr: 1, verticalAlign: 'middle' }} />
                      Date: Pending
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
        
        {canSign && (
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: 1.5 }}>
            {formError && <Alert severity="error" sx={{ width: '100%' }}>{formError}</Alert>}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              {hasProposedChanges
                ? `You changed some rates, so this sends your proposal to ${orgName} for review.`
                : 'No rates changed, so signing accepts these rates and finalizes the agreement.'}
            </Typography>
            <Button
              variant="contained"
              sx={styles.primaryButton}
              startIcon={<SendIcon />}
              onClick={handleSubmit}
              disabled={loading}
            >
              {hasProposedChanges ? 'Sign & Submit Proposed Rates' : 'Accept Rates & Sign'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Enhanced Terms and Conditions */}
      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }} elevation={3}>
        <Typography variant="h5" sx={styles.sectionTitle}>Terms and Conditions</Typography>
        <Typography variant="body2" paragraph>
          By signing this rate agreement, you acknowledge that you have reviewed and agree to the rates listed above.
          Any proposed rates are subject to review and approval by {legalName}. Once approved, these rates will be
          applied to all future services provided at this facility.
        </Typography>
        <Typography variant="body2" paragraph>
          This agreement shall remain in effect for one year from the date of execution and may be renewed or renegotiated upon mutual agreement.
          Either party may terminate this agreement with thirty (30) days written notice.
        </Typography>
        <Typography variant="body2">
          The Service Provider agrees to maintain appropriate insurance coverage and licensure as required by applicable laws and to represent
          {legalName} in a professional manner at all times.
        </Typography>
      </Paper>
      
      {/* Action Buttons moved to bottom */}
      <Paper sx={{ p: 3, mb: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }} elevation={3}>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{
            backgroundColor: themeColors.primary,
            color: 'white',
            padding: '10px 20px',
            '&:hover': {
              backgroundColor: '#2a3490'
            }
          }}
        >
          Print Agreement
        </Button>
        <Button
          variant="contained"
          startIcon={<PdfIcon />}
          onClick={handleDownloadPDF}
          sx={{
            backgroundColor: themeColors.primary,
            color: 'white',
            padding: '10px 20px',
            '&:hover': {
              backgroundColor: '#2a3490'
            }
          }}
        >
          Download PDF
        </Button>
        <Button
          variant="contained"
          startIcon={<ExcelIcon />}
          onClick={handleDownloadExcel}
          sx={{
            backgroundColor: themeColors.primary,
            color: 'white',
            padding: '10px 20px',
            '&:hover': {
              backgroundColor: '#2a3490'
            }
          }}
        >
          Download Excel
        </Button>
      </Paper>
      
      {/* Footer with Contact Information */}
      <Box sx={styles.footer}>
        <Typography variant="body2">
          {isPlatform
            ? `${legalName}, 333 Texas St, Ste. 1300, Shreveport, LA 71101, (318) 759-7619`
            : `${legalName} · ${supportEmail} · ${supportPhone}`}
        </Typography>
      </Box>
    </Box>
  );
};

export default RateAgreementPage;
